import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck, Hammer, Plane, Activity } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import FAQ from "@/components/FAQ";

const STAGES = [
  {
    icon: ClipboardCheck,
    n: "01",
    week: "Week 1",
    title: "Growth Audit",
    body: "A 60-minute deep-dive video call with a senior advisor. We map your current website, lead flow, follow-up cadence, and reporting. You receive a one-page Growth Diagnosis: the single largest leak in your lead-to-revenue funnel, and what it&rsquo;s costing you per month.",
    you: ["Show us your current setup (45–60 mins).", "Share recent lead numbers, ad spend, conversion rates."],
    we: ["Run the audit and benchmark vs operators in your trade.", "Deliver the Growth Diagnosis & a fixed-fee build quote within 48 hours."],
  },
  {
    icon: Hammer,
    n: "02",
    week: "Weeks 2–6",
    title: "Build Sprint",
    body: "Five-week, fixed-fee build. We design and ship the new site, configure lead-capture and the CRM workflows, build your KPI dashboards, and train the AI assistant on your trade, services, pricing tiers and FAQ. Daily progress updates. Weekly working sessions.",
    you: ["~2 hours per week of input (review, approve, sign off).", "Access to current site, customer list, existing tools."],
    we: ["Design, build, configure, integrate, test the entire system.", "Weekly Loom walkthroughs of progress."],
  },
  {
    icon: Plane,
    n: "03",
    week: "Week 7",
    title: "Migration & Go-Live",
    body: "Domain cutover. Customer data migrated. Phone/SMS numbers ported if needed. The team gets a 90-minute training session on the new system. Two weeks of hand-holding while the first leads flow through and any edge cases get caught.",
    you: ["1.5h team training session.", "Tell us when to flip the switch."],
    we: ["DNS, data migration, integrations, QA.", "14 days of priority support during stabilisation."],
  },
  {
    icon: Activity,
    n: "04",
    week: "Week 8 onwards",
    title: "Run-Mode",
    body: "Our outsourced execution team runs the system day-to-day. Quote follow-up calls, CRM hygiene, ad campaign management, content posting, review chasing, monthly reporting. You get weekly performance snapshots and a monthly strategy review.",
    you: ["A monthly 45-min strategy review with your account lead.", "Stay on the tools."],
    we: ["Daily operations of the entire system.", "Weekly reports, monthly review, quarterly roadmap."],
  },
];

const FAQS = [
  {
    q: "Is the build fee fixed?",
    a: "Yes. The audit produces a written scope and a fixed-fee build quote. Out-of-scope additions (e.g., a custom integration you ask for mid-build) are quoted separately and only proceed with your written approval.",
  },
  {
    q: "What does the monthly run-fee cover?",
    a: "Hosting and platform costs, ongoing security and maintenance, AI assistant operations, the outsourced execution team's hours (typically 15–30 per week depending on your tier), reporting, and the monthly strategy review. No per-lead fees, no commissions.",
  },
  {
    q: "Can I cancel the run-fee at any time?",
    a: "Yes, monthly rolling after the first 90 days. You keep the website, CRM, data, and all platform accounts. We hand over cleanly. We just stop operating it for you.",
  },
  {
    q: "How much of my time does it take?",
    a: "Around 2 hours/week during the 5-week build (mostly approvals). 1.5h training at go-live. Then 45 minutes/month for the strategy review. Less than a single call-out.",
  },
  {
    q: "What if I already have a CRM / website / ads I like?",
    a: "We assess what's working in the audit and reuse it where possible. The growth system is the operating model, the specific tools underneath are flexible.",
  },
  {
    q: "Do you guarantee outcomes?",
    a: "We don't guarantee revenue (anyone who does is lying, too many variables in your hands). We do guarantee delivery of the build to spec, the run-mode service levels, and a commitment to iterate the system until conversion lifts. If we can't move the needle in 90 days post-launch, we'll work for free until we do.",
  },
];

export default function HowItWorks() {
  return (
    <>
      <section className="container-x pt-24 md:pt-32 pb-12" data-testid="how-hero">
        <div className="overline">How it works</div>
        <h1 className="mt-6 max-w-4xl font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl text-balance">
          From audit to autopilot in 60 days.
        </h1>
        <p className="mt-8 max-w-2xl text-zinc-300 leading-relaxed text-lg">
          A clear, fixed-fee path from your current setup to a fully-running
          growth system, and a team that operates it for you. No surprises,
          no scope creep, no ambiguity.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Link to="/contact" className="btn-gold" data-testid="how-cta-book">
            Book your Growth Audit <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* TIMELINE STAGES */}
      <section className="section-y" data-testid="how-timeline">
        <div className="container-x space-y-px bg-white/[0.05]">
          {STAGES.map((s, i) => (
            <Reveal key={s.n}>
              <div className="bg-ink p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid={`how-stage-${i}`}>
                <div className="lg:col-span-4">
                  <div className="flex items-center gap-3">
                    <span className="overline">{s.n}</span>
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">{s.week}</span>
                  </div>
                  <h3 className="mt-6 text-2xl md:text-3xl font-display font-light tracking-tight">{s.title}</h3>
                  <s.icon className="mt-6 w-6 h-6 text-gold" />
                </div>
                <div className="lg:col-span-5">
                  <p className="text-zinc-300 leading-relaxed">{s.body}</p>
                </div>
                <div className="lg:col-span-3 grid grid-cols-1 gap-6">
                  <div>
                    <div className="overline text-zinc-500">You</div>
                    <ul className="mt-3 space-y-2 text-sm text-zinc-400">
                      {s.you.map((y) => (
                        <li key={y} className="flex items-start gap-2">
                          <span className="mt-2 inline-block w-1 h-1 bg-zinc-600 flex-shrink-0" />
                          <span>{y}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="overline">Toulios</div>
                    <ul className="mt-3 space-y-2 text-sm text-zinc-300">
                      {s.we.map((w) => (
                        <li key={w} className="flex items-start gap-2">
                          <span className="mt-2 inline-block w-1 h-1 bg-gold flex-shrink-0" />
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INVESTMENT */}
      <section className="section-y bg-[#070707] border-y border-white/[0.05]" data-testid="how-investment">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <SectionHeading
            eyebrow="Investment"
            title="One fixed setup fee. One transparent monthly fee. That&rsquo;s it."
            description="No equity. No per-lead fees. No revenue-share. No surprises in the invoice."
          />
          <div className="space-y-px bg-white/[0.05]">
            {[
              { k: "Growth Audit", v: "Complimentary 60-minute call + written 1-page diagnosis." },
              { k: "Setup (Weeks 2–7)", v: "Fixed fee, quoted in writing after the audit. Payment terms: 50% on start, 50% on go-live (Net 14)." },
              { k: "Run-Mode (Week 8+)", v: "Transparent monthly fee covering platform, ops team, ads, reporting and strategy review. Monthly rolling after 90 days." },
              { k: "Out of scope", v: "We never charge for outcomes (commissions). We never charge per lead. We never ask for equity." },
            ].map((row) => (
              <div key={row.k} className="bg-ink grid grid-cols-12 gap-6 p-6 md:p-8" data-testid={`how-invest-${row.k.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                <div className="col-span-4 overline">{row.k}</div>
                <div className="col-span-8 text-zinc-300 leading-relaxed">{row.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y" data-testid="how-faq-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="FAQ"
              title="Process questions, answered."
              description="The questions every operator asks before signing. Straight answers."
            />
          </div>
          <div className="lg:col-span-7">
            <FAQ items={FAQS} testIdPrefix="how-faq" />
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
